import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UploadSimple, ArrowRight, CheckCircle, Spinner, Briefcase, Tag } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackFormSubmit } from "@/hooks/useTrafficTracker";
import { BUSINESS } from "@/data/site";

const initial = {
  name: "",
  address: "",
  phone: "",
  email: "",
  professional_reference: "",
  recent_job: "",
  notes: "",
};

const MAX_MB = 5;

export default function CareersPage() {
  const [form, setForm] = useState(initial);
  const [resume, setResume] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) { setResume(null); return; }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Resume too large. Max ${MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    setResume(f);
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.address.trim()) e.address = "Required";
    if (!form.phone.trim() || form.phone.replace(/\D/g, "").length < 7) e.phone = "Valid phone required";
    if (!form.professional_reference.trim() || form.professional_reference.trim().length < 5) e.professional_reference = "Please share at least one reference (name, relationship, phone or email)";
    if (!form.recent_job.trim() || form.recent_job.trim().length < 5) e.recent_job = "Please describe one recent job";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error("Please fix the highlighted fields."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (resume) fd.append("resume", resume);
      await api.post("/career-applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      trackFormSubmit("career-application");
      toast.success("Application received — thanks for applying!");
      setSubmitted(true);
      setForm(initial); setResume(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="careers-page">
      {/* Hero */}
      <section className="ji-container pt-16 md:pt-24 pb-10">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Briefcase size={12} weight="fill" /> Now Hiring · Southern Louisiana
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Join the crew —<br /><span className="text-[var(--ji-accent)] italic">work you can be proud of.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          Joseph Industries is a family-owned business built on hard work and honest service. If you show up, do the work, and treat customers right — we'd love to hear from you.
        </p>
      </section>

      {/* Two-column */}
      <section className="ji-container pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="ji-card p-7 md:p-10" style={{ borderRadius: 4 }}>
            {submitted ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="py-10 text-center" data-testid="careers-success">
                <CheckCircle size={44} weight="duotone" className="text-[var(--ji-accent)] mx-auto" />
                <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-4 text-3xl font-bold tracking-tight">Application received.</h2>
                <p className="mt-3 text-[var(--ji-muted)]">Thanks for applying. David will review your application and reach out if it's a fit.</p>
                <button data-testid="careers-new-submission" onClick={() => setSubmitted(false)} className="ji-btn-outline mt-8 inline-flex items-center gap-2 px-6 h-12 text-sm font-semibold" style={{ borderRadius: 4 }}>
                  Submit another application
                </button>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} data-testid="careers-form" className="space-y-5" encType="multipart/form-data">
                <div className="ji-overline inline-flex items-center gap-2"><Tag size={12} weight="fill" /> Job Application</div>
                <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight">
                  Tell us about you.
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                  <div>
                    <Label htmlFor="c-name" className="text-sm font-medium">Full name *</Label>
                    <Input id="c-name" data-testid="careers-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 mt-2" placeholder="Jane Doe" />
                    {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="text-sm font-medium">Phone *</Label>
                    <Input id="c-phone" data-testid="careers-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12 mt-2" placeholder="(504) 555-0100" />
                    {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="c-address" className="text-sm font-medium">Address *</Label>
                    <Input id="c-address" data-testid="careers-address" value={form.address} onChange={(e) => update("address", e.target.value)} className="h-12 mt-2" placeholder="Street, City, LA" />
                    {errors.address && <p className="text-xs text-red-600 mt-1">{errors.address}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="c-email" className="text-sm font-medium">Email (optional)</Label>
                    <Input id="c-email" type="email" data-testid="careers-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 mt-2" placeholder="you@email.com" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="c-ref" className="text-sm font-medium">Professional reference *</Label>
                    <Textarea id="c-ref" data-testid="careers-reference" value={form.professional_reference} onChange={(e) => update("professional_reference", e.target.value)} className="mt-2 min-h-[100px]" placeholder="Name, relationship, phone or email" />
                    {errors.professional_reference && <p className="text-xs text-red-600 mt-1">{errors.professional_reference}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="c-job" className="text-sm font-medium">Recent job *</Label>
                    <Textarea id="c-job" data-testid="careers-recent-job" value={form.recent_job} onChange={(e) => update("recent_job", e.target.value)} className="mt-2 min-h-[100px]" placeholder="Company, role, dates, and what you did" />
                    {errors.recent_job && <p className="text-xs text-red-600 mt-1">{errors.recent_job}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="c-notes" className="text-sm font-medium">Anything else? (optional)</Label>
                    <Textarea id="c-notes" data-testid="careers-notes" value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-2 min-h-[80px]" placeholder="Availability, certifications, why us…" />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="c-resume" className="text-sm font-medium inline-flex items-center gap-2">
                      <UploadSimple size={14} weight="bold" /> Resume (optional · PDF/DOC/DOCX/PNG/JPG · max {MAX_MB}MB)
                    </Label>
                    <input
                      id="c-resume"
                      type="file"
                      data-testid="careers-resume"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
                      onChange={onFileChange}
                      className="mt-2 block w-full text-sm text-[var(--ji-muted)] file:mr-4 file:py-2.5 file:px-4 file:border-0 file:bg-[var(--ji-primary)] file:text-white file:text-sm file:font-medium hover:file:bg-[var(--ji-primary-hover)] file:cursor-pointer"
                    />
                    {resume && (
                      <div className="mt-2 text-xs text-[var(--ji-muted)]" data-testid="careers-resume-selected">
                        Selected: <span className="text-[var(--ji-text)] font-medium">{resume.name}</span> · {(resume.size / 1024).toFixed(0)} KB
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-2">
                  <button type="submit" data-testid="careers-submit" disabled={submitting}
                    className="ji-btn-primary inline-flex items-center justify-center gap-2 px-6 h-14 text-base font-semibold disabled:opacity-60"
                    style={{ borderRadius: 4 }}
                  >
                    {submitting ? <><Spinner size={18} className="animate-spin" /> Submitting…</> : <>Submit application <ArrowRight size={18} weight="bold" /></>}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="ji-card p-7" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-4">What we look for</div>
            <ul className="space-y-3 text-sm text-[var(--ji-text)]">
              {[
                "Reliability — you show up on time",
                "Honesty — you say what you'll do, and do it",
                "Craft — you take pride in the work",
                "Comfortable lifting, driving, using tools",
                "Louisiana driver's license & clean record",
              ].map((it) => (
                <li key={it} className="flex items-start gap-2">
                  <CheckCircle size={14} weight="duotone" className="text-[var(--ji-accent)] mt-1" /> {it}
                </li>
              ))}
            </ul>
          </div>

          <div className="ji-card p-7" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-4">Positions</div>
            <ul className="space-y-2 text-sm">
              <li>· Appliance Technician / Installer</li>
              <li>· Courier Driver</li>
              <li>· Home Services Crew (mounting · washing · painting)</li>
              <li>· General Helper / Apprentice</li>
            </ul>
          </div>

          <div className="ji-card p-7" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-4">Questions?</div>
            <p className="text-sm text-[var(--ji-muted)] leading-relaxed">
              Call David directly at{" "}
              <a href={`tel:${BUSINESS.phones[0].tel}`} className="text-[var(--ji-primary)] font-semibold hover:text-[var(--ji-accent)]">
                {BUSINESS.phones[0].display}
              </a>{" "}
              or email{" "}
              <a href={`mailto:${BUSINESS.emails[0]}`} className="text-[var(--ji-primary)] font-semibold hover:text-[var(--ji-accent)] break-all">
                {BUSINESS.emails[0]}
              </a>.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}
